import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRadixConnect } from '../../radix_connect'; // Adjust path if needed
import { getRadcoonSessionToken, recordWithdrawal } from '../../backend'; // Adjust path if needed
import {
  getSupabaseConfigError,
  isSupabaseConfigured,
} from '../../backend/supabase';
import { formatTokenAmount } from '../../utils/formatTokenAmount';
import './MiningRigMenu.scss'; // Reuse styles or create SettingsMenu.scss

// Settings Menu Component
const SettingsMenu = ({ gameSave, onClose, reloadGameData, refreshMoneyDisplay }) => {
  const navigate = useNavigate();
  const { selectedAccount } = useRadixConnect();
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [withdrawError, setWithdrawError] = useState(null);
  const [withdrawSuccess, setWithdrawSuccess] = useState(null);

  const balance = gameSave?.money || 0;
  const saveId = gameSave?.id; // Assuming gameSave has the DB record ID as 'id'
  const rewardTokenSymbol = gameSave?.constants?.REWARD_TOKEN_SYMBOL || 'DCKSLAP';
  const MINIMUM_WITHDRAWAL_AMOUNT = gameSave?.constants?.MINIMUM_WITHDRAWAL_AMOUNT ?? 20;
  const connectedWalletAddress = selectedAccount?.address || null;
  const hasRadcoonSession = Boolean(getRadcoonSessionToken());

  const handleExitGame = () => {
    // Release pointer lock if active
    if (document.pointerLockElement) {
      document.exitPointerLock();
    }

    // Small delay to ensure pointer lock is fully released before navigation
    setTimeout(() => {
      // Set skipRedirect flag to show company list when returning to StartScreen
      localStorage.setItem('skipRedirect', 'true');
      navigate('/start'); 
    }, 100);
  };

  const handleWithdraw = useCallback(async () => {
    if (balance < MINIMUM_WITHDRAWAL_AMOUNT || !saveId || isWithdrawing) {
      console.warn('Withdraw conditions not met:', { balance, saveId, isWithdrawing, minAmount: MINIMUM_WITHDRAWAL_AMOUNT });
      return;
    }

    if (!hasRadcoonSession) {
      setWithdrawError('Reconnect your wallet session to withdraw.');
      setWithdrawSuccess(null);
      return;
    }

    if (!isSupabaseConfigured) {
      setWithdrawError(getSupabaseConfigError());
      setWithdrawSuccess(null);
      return;
    }

    setIsWithdrawing(true);
    setWithdrawError(null);
    setWithdrawSuccess(null);

    try {
      const updateResult = await recordWithdrawal(
        saveId,
        balance,
        null,
        connectedWalletAddress
      );

      if (updateResult.error) {
        // Notify user that backend update failed, transaction might still succeed on chain
        setWithdrawError(`Transaction submitted, but backend update failed: ${updateResult.error.message || 'Unknown error'}`);
      } else {
        setWithdrawError(null);

        if (refreshMoneyDisplay) {
          refreshMoneyDisplay(0);
        }

        if (reloadGameData) {
          await reloadGameData();
        }

        const transactionId = updateResult.transactionId || updateResult.txId;
        setWithdrawSuccess(
          transactionId
            ? `Withdrawal successful! Transaction ID: ${transactionId.substring(0, 10)}...`
            : 'Withdrawal successful!'
        );
      }

    } catch (error) {
      // Catch errors from sendTransaction or other issues
      console.error('Withdrawal process error:', error);
      setWithdrawError(error.message || 'An unexpected error occurred during withdrawal.');
      // Clear success message if an error occurs
      setWithdrawSuccess(null); 
    } finally {
      setIsWithdrawing(false);
    }
  }, [
    balance,
    saveId,
    isWithdrawing,
    MINIMUM_WITHDRAWAL_AMOUNT,
    hasRadcoonSession,
    connectedWalletAddress,
    reloadGameData,
    refreshMoneyDisplay,
  ]);


  return (
    <div className="settings-menu mining-rig-menu"> {/* Reuse existing styles */} 
      <div className="settings-content mining-rig-content"> {/* Reuse existing styles */} 
        <button className="close-button" onClick={onClose} aria-label="Close">x</button>
        <h2>Settings</h2>

        <div className="menu-options">
          {/* Existing Go Back Button */} 
          <button 
            className="menu-button"
            onClick={handleExitGame}
            disabled={isWithdrawing} // Disable while withdrawing
          >
            Go Back to Saves
          </button>

          {/* Withdraw Section */} 
          <div className="withdraw-section">
            <h4>Withdraw {rewardTokenSymbol}</h4>
            <p>Current Balance: {formatTokenAmount(balance)} {rewardTokenSymbol}</p>
            <button 
              className="menu-button withdraw-button" 
              onClick={handleWithdraw}
              // Update disabled condition using the constant from props
              disabled={
                balance < MINIMUM_WITHDRAWAL_AMOUNT ||
                !hasRadcoonSession ||
                isWithdrawing ||
                !saveId ||
                !isSupabaseConfigured
              }
            >
              {isWithdrawing ? 'Withdrawing...' : `Withdraw All (${formatTokenAmount(balance)})`}
            </button>
            {!isSupabaseConfigured && (
              <p className="error-message">Withdrawals are offline right now.</p>
            )}
            {balance > 0 && balance < MINIMUM_WITHDRAWAL_AMOUNT && (
              <p className="info-message">Minimum withdrawal amount is {formatTokenAmount(MINIMUM_WITHDRAWAL_AMOUNT)} {rewardTokenSymbol}.</p>
            )}
            {balance <= 0 && <p className="info-message">No balance to withdraw.</p>}
            {balance >= MINIMUM_WITHDRAWAL_AMOUNT && !hasRadcoonSession && (
              <p className="error-message">Reconnect your wallet session to withdraw.</p>
            )}
            {!saveId && hasRadcoonSession && <p className="error-message">Error: Cannot identify game save.</p>}
          </div>

          {/* Feedback Messages */} 
          {withdrawSuccess && <p className="success-message">{withdrawSuccess}</p>}
          {withdrawError && <p className="error-message">Error: {withdrawError}</p>}
        </div>

        <div className="credits">
          Reddicks Games
        </div>
      </div>
    </div>
  );
};

export default SettingsMenu; 
